import Button from "@/ui/components/Button"
import { CommonInput } from "@/components/common/element/fields/CommonInput"
import { InlineCurrencyAmountInput } from "@/ui/components/InlineCurrencyAmountInput"
import * as TrinityAPI from "@/requests/TrinityAPI"

import type { IUser } from "@/types/user/IUser"
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react"

import style from "../style.module.scss"
import { ModalPopUp } from "@/components/common/element/ModalPopUp"
import { IModalMessage } from "@/types/error/IModalMessage"
import { spacedNum } from "@/components/common/CommonHelper"
import { IExchangeDto } from "@/types/exchange/IExchangeDto"

type ActiveField = "from" | "to"

type Props = {
  user: IUser | null
  onClose: () => void
  refetch: () => void
}

export const CurrencyExchangeForm: FC<Props> = ({ user, onClose, refetch }) => {
  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)
  const [fromAmount, setFromAmount] = useState("")
  const [toAmount, setToAmount] = useState("")
  const [fromCurrency, setFromCurrency] = useState("RUB")
  const [toCurrency, setToCurrency] = useState("USD")
  const [rate, setRate] = useState("")
  const [signOverride, setSignOverride] = useState<"*" | "/">("*")
  const [activeField, setActiveField] = useState<ActiveField>("from")
  const [userBalances, setUserBalances] = useState<Record<string, number> | null>(null)
  const [isExchangeLoading, setExchangeLoading] = useState(false)

  const reqIdRef = useRef(0)

  const formatBalance = (currency: string) => {
    const raw = userBalances?.[currency]
    if (!raw) return `Баланс: 0`
    return `Баланс: ${spacedNum((raw / 100).toFixed(2))} ${currency}`
  }

  const isOverBalance = useMemo(() => {
    if (!fromAmount || !userBalances) return false
    const balance = (userBalances[fromCurrency] ?? 0) / 100
    return Number(fromAmount) > balance
  }, [fromAmount, fromCurrency, userBalances])

  const derivedRateHint = useMemo(() => {
    const r = Number(rate)
    if (!r || !fromCurrency || !toCurrency) return ""
    const display = signOverride === "*" ? r : 1 / r
    return `1 ${fromCurrency} = ${display.toFixed(6).replace(/\.?0+$/, "")} ${toCurrency}`
  }, [rate, signOverride, fromCurrency, toCurrency])

  const isExchangeDisabled = useMemo(() => {
    return (
      !fromAmount ||
      !rate ||
      !fromCurrency ||
      !toCurrency ||
      !toAmount ||
      !user?.userId ||
      isOverBalance ||
      fromCurrency === toCurrency
    )
  }, [fromAmount, rate, fromCurrency, toCurrency, toAmount, user?.userId, isOverBalance])

  const runCalculation = useCallback(
    (field: ActiveField, fAmount: string, tAmount: string, fCur: string, tCur: string, r: string, sign: "*" | "/") => {
      const sourceAmount = field === "from" ? fAmount : tAmount
      if (!sourceAmount || !r || !fCur || !tCur) {
        if (field === "from") setToAmount("")
        else setFromAmount("")
        return
      }

      const amountInCents = Math.round(Number(sourceAmount) * 100)
      const id = ++reqIdRef.current

      const payload: IExchangeDto = {
        amount: amountInCents.toString(),
        from: field === "from" ? fCur : tCur,
        to: field === "from" ? tCur : fCur,
        exchangeRate: r,
        signOverride: sign,
        direction: field === "from" ? "direct" : "reverse"
      }

      TrinityAPI.calculateExchangeRate(payload, response => {
        if (reqIdRef.current !== id) return
        if (response.status === 200) {
          const result = (Number(response.data.amount) / 100).toFixed(2)
          if (field === "from") setToAmount(result)
          else setFromAmount(result)
        } else {
          if (field === "from") setToAmount("")
          else setFromAmount("")
        }
      })
    },
    []
  )

  // Debounced recalculation
  useEffect(() => {
    const timer = setTimeout(() => {
      runCalculation(activeField, fromAmount, toAmount, fromCurrency, toCurrency, rate, signOverride)
    }, 300)
    return () => clearTimeout(timer)
  }, [fromAmount, toAmount, fromCurrency, toCurrency, rate, signOverride, activeField])

  // Load user balances
  useEffect(() => {
    if (user?.userId) {
      TrinityAPI.getAdminUserBalances(user.userId, response => {
        if (response.status === 200) {
          setUserBalances(response.data)
        } else {
          setModalMessage({ title: "Ошибка!", text: "Ошибка загрузки балансов: " + response.data.message })
        }
      })
    }
  }, [user?.userId])

  const handleFromAmountChange = (v: string) => {
    setActiveField("from")
    setFromAmount(v)
  }

  const handleToAmountChange = (v: string) => {
    setActiveField("to")
    setToAmount(v)
  }

  const handleSwap = () => {
    const prevTo = toAmount
    const prevFromCur = fromCurrency
    const prevToCur = toCurrency

    setFromCurrency(prevToCur)
    setToCurrency(prevFromCur)
    setFromAmount(prevTo)
    setToAmount("")
    setActiveField("from")
  }

  const handleExchange = () => {
    setExchangeLoading(true)

    const amountInCents = Math.round(Number(fromAmount) * 100)

    const payload: IExchangeDto = {
      userId: user?.userId || 0,
      amount: amountInCents.toString(),
      from: fromCurrency,
      to: toCurrency,
      exchangeRate: rate,
      signOverride,
      direction: "direct"
    }

    TrinityAPI.exchangeUserBalance(
      payload,
      response => {
        if (response.status === 200) {
          window.location.reload()
        } else {
          setModalMessage({ title: "Ошибка!", text: "Ошибка обмена: " + response.data.message })
        }
      },
      () => setExchangeLoading(false)
    )
  }

  if (!user) {
    return null
  }

  return (
    <>
      <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} error={undefined} />
      <section className="popup-forms index-top" id="error-popup">
        <div className="container-popup-forms">
          <button className="close-btn-card" id="password-close" onClick={onClose} />
          <div className="popup-card">
            <h4 className="heading-chapter">Обмен валют — {user.username}</h4>

            {userBalances && Object.keys(userBalances).length > 0 && (
              <div className={style.balanceChips}>
                {Object.keys(userBalances).map(key => (
                  <div key={key} className={style.balanceChip}>
                    {key}: <strong>{spacedNum(((userBalances[key] || 0) / 100).toFixed(2))}</strong>
                  </div>
                ))}
              </div>
            )}

            <div className={style.exchangeRow}>
              <InlineCurrencyAmountInput
                amount={fromAmount}
                currency={fromCurrency}
                onAmountChange={handleFromAmountChange}
                onCurrencyChange={v => {
                  setFromCurrency(v)
                  setActiveField("from")
                }}
                balanceHint={formatBalance(fromCurrency)}
                error={isOverBalance}
                disabledCurrency={toCurrency}
              />

              <button type="button" className={style.swapButton} onClick={handleSwap} title="Поменять валюты местами">
                ⇄
              </button>

              <InlineCurrencyAmountInput
                amount={toAmount}
                currency={toCurrency}
                onAmountChange={handleToAmountChange}
                onCurrencyChange={v => {
                  setToCurrency(v)
                  setActiveField("from")
                }}
                balanceHint={formatBalance(toCurrency)}
                disabledCurrency={fromCurrency}
              />
            </div>

            <div className={style.rateRow}>
              <div className={style.rateInputWrap}>
                <CommonInput
                  typeName="number"
                  label="Курс"
                  placeholder="Введите курс"
                  name="rate"
                  value={rate}
                  errorModel={undefined}
                  _callback={v => setRate((v || "").replace(/\s/g, ""))}
                />
              </div>
              <div className={style.signToggle}>
                <legend>Знак</legend>
                <div className={style.signButtons}>
                  <button
                    type="button"
                    className={signOverride === "*" ? style.signActive : style.signInactive}
                    onClick={() => setSignOverride("*")}
                  >
                    ×
                  </button>
                  <button
                    type="button"
                    className={signOverride === "/" ? style.signActive : style.signInactive}
                    onClick={() => setSignOverride("/")}
                  >
                    ÷
                  </button>
                </div>
              </div>
              {derivedRateHint && <div className={style.rateHint}>{derivedRateHint}</div>}
            </div>

            <div className="flex-row-24">
              <Button disabled={isExchangeDisabled} loading={isExchangeLoading} onClick={handleExchange}>
                Обменять
              </Button>
              <Button variant="grey" onClick={onClose}>
                Закрыть
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
